import { ErrorMessage } from '@hookform/error-message';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { RHFSwitchField } from '@openx/components-core/lib/SwitchField/RHFSwitchField.tsx';
import { RHFTextFieldWithAdornment } from '@openx/components-core/lib/TextField/RHFTextFieldWithAdornment.tsx';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form.ts';
import { PackageFields, PrivateMarketFields } from '@openx/types/package.ts';
import { currencySymbol } from '@openx/utils/lib/priceFormat.ts';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PRIVATE_MARKET_DESCRIPTION, packageLabels } from '../constants.ts';

type PrivateMarketProps = {
  currency: string;
};

export function PrivateMarketSection({ currency }: PrivateMarketProps) {
  const { t } = useTranslation();
  const {
    formState: { errors },
    trigger,
  } = useFormContext();

  const observableDiscoverable = useWatch({
    name: `${PackageFields.PRIVATE_MARKET}.${PrivateMarketFields.DISCOVERABLE}`,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: all necessary dependencies are included for domain triggering logic
  useEffect(() => {
    trigger(PackageFields.DOMAINS);
  }, [observableDiscoverable, trigger]);

  return (
    <FormSection
      divider
      title={t('Private Marketplace Package Rates')}
      description={PRIVATE_MARKET_DESCRIPTION(t)}
      customGrid
      name="PrivateMarket"
    >
      <Grid size={RIGHT_COLUMN_WIDTH}>
        <RHFTextFieldWithAdornment
          dataTest="package-pm"
          formControlProps={{ margin: 'dense' }}
          label={packageLabels(t)[PrivateMarketFields.DEFAULT_RATE]}
          name={`${PackageFields.PRIVATE_MARKET}.${PrivateMarketFields.DEFAULT_RATE}`}
          placeholder="0.00"
          startAdornment={currencySymbol(currency)}
          slotProps={{ input: { sx: { marginTop: 0 } } }}
        />

        <RHFTextFieldWithAdornment
          dataTest="package-pm-discounted-rate"
          formControlProps={{ margin: 'dense' }}
          label={packageLabels(t)[PrivateMarketFields.DISCOUNTED_RATE]}
          name={`${PackageFields.PRIVATE_MARKET}.${PrivateMarketFields.DISCOUNTED_RATE}`}
          placeholder="0.00"
          startAdornment={currencySymbol(currency)}
          slotProps={{ input: { sx: { marginTop: 0 } } }}
        />

        <RHFSwitchField
          name={`${PackageFields.PRIVATE_MARKET}.${PrivateMarketFields.DISCOVERABLE}`}
          label={packageLabels(t)[PrivateMarketFields.DISCOVERABLE]}
          type="checkbox"
          data-test="package-pm-discoverable"
          checked={observableDiscoverable}
        />
      </Grid>
      <Grid container>
        <Grid size={3} />
        <Grid size={9}>
          <ErrorMessage
            name={PackageFields.PRIVATE_MARKET}
            errors={errors}
            render={errorObj => {
              return typeof errorObj.message === 'string' && <FormHelperText error>{errorObj.message}</FormHelperText>;
            }}
          />
        </Grid>
      </Grid>
    </FormSection>
  );
}
